import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { addProductToCart, getProductRecommendationsV2 } from './useSupabase';
import { useAuth } from './useAuth';
import { getPrimaryProductImageUrl } from '../utils/imageUtils';
import { getUserWishlistV2, deleteUserWishlistV2, useWishlist } from './useWishlist';
import type { Product } from '../components/ProductDetail/types';
import { useWishlistContext } from './useWishlistContext';

export const useProductDetail = () => {
  const { addToWishlist } = useWishlist();
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { userId, user } = useAuth();
  
  // State for product data
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // State for wishlist
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const { refreshWishlist } = useWishlistContext();
  
  // State for product interactions
  const [quantity, setQuantity] = useState(1);
  const [mainImage, setMainImage] = useState<string>('');
  const [selectedTab, setSelectedTab] = useState('description');
  
  // State for recommended products
  const [recommendedProducts, setRecommendedProducts] = useState<any[]>([]);
  const [recommendedLoading, setRecommendedLoading] = useState(true);

  // Fetch product data
  useEffect(() => {
    if (!productId) {
      setError('Product ID is required');
      setLoading(false);
      return;
    }

    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data: detailedProduct, error: fetchError } = await supabase.rpc(
          'fetch_product_with_images_by_id',
          {
            p_product_id: parseInt(productId),
            p_locale: 'en',
          }
        );

        if (fetchError) {
          if (fetchError.code === 'PGRST116') {
            setError('Product not found');
          } else {
            throw fetchError;
          }
          return;
        }

        setProduct(detailedProduct);
        
        if (
          detailedProduct &&
          Array.isArray(detailedProduct.image_paths) &&
          detailedProduct.image_paths.length > 0
        ) {
          setMainImage(getPrimaryProductImageUrl(detailedProduct.image_paths));
        } else {
          setMainImage('');
        }
      } catch (err) {
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  // Fetch recommended products
  useEffect(() => {
    const fetchRecommended = async () => {
      if (!productId) return;
      setRecommendedLoading(true);
      try {
        const response = await getProductRecommendationsV2({
          product_id: Number(productId),
        });
        let recommended: any[] = [];
        if (
          response &&
          typeof response === 'object' &&
          'data' in response &&
          Array.isArray((response as any).data)
        ) {
          recommended = (response as any).data;
        }
        setRecommendedProducts(recommended);
      } catch {
        setRecommendedProducts([]);
      } finally {
        setRecommendedLoading(false);
      }
    };
    fetchRecommended();
  }, [productId]);

  // Check if product is in wishlist
  useEffect(() => {
    const checkWishlist = async () => {
      if (!userId || !productId) return;
      try {
        const payload = { user_id: userId };
        const response = await getUserWishlistV2(payload);
        const items = response?.data?.items || [];
        setIsWishlisted(
          items.some((item: any) => item.id === Number(productId))
        );
      } catch (e) {
        setIsWishlisted(false);
      }
    };
    checkWishlist();
  }, [userId, productId]);

  // Price calculations
  const calculateDiscountedPrice = (
    price: number,
    saleRate?: number
  ): number => {
    if (!saleRate || saleRate <= 0) return price;
    return price * (1 - saleRate / 100);
  };

  const originalPrice = product?.usd_price || 0;
  const discountedPrice = calculateDiscountedPrice(
    originalPrice,
    product?.sale_rate
  );
  const hasDiscount = Boolean(product?.sale_rate && product?.sale_rate > 0);

  // Handlers
  const handleQuantityChange = (type: 'increment' | 'decrement') => {
    if (type === 'increment') {
      setQuantity(quantity + 1);
    } else {
      setQuantity(quantity > 1 ? quantity - 1 : quantity);
    }
  };

  const handleAddToCart = async () => {
    if (!userId) {
      navigate('/registration');
      return;
    }
    if (!product) {
      return;
    }
    try {
      await addProductToCart({ productId: product.id, quantity });
      navigate('/cart');
    } catch (err) {
      alert('Failed to add to cart.');
    }
  };

  const handleWishlistClick = async () => {
    if (!user) {
      navigate('/registration');
      return;
    }
    if (!product) return;
    setWishlistLoading(true);
    try {
      if (isWishlisted) {
        await deleteUserWishlistV2({ user_id: userId, product_id: product.id });
        setIsWishlisted(false);
      } else {
        await addToWishlist(product.id);
        setIsWishlisted(true);
      }
      await refreshWishlist();
    } catch (e) {
      // Optionally show error
    } finally {
      setWishlistLoading(false);
    }
  };

  const handleImageChange = (image: string) => {
    setMainImage(image);
  };

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleBackToProducts = () => {
    navigate('/products');
  };

  return {
    // Data
    product,
    loading,
    error,
    quantity,
    mainImage,
    selectedTab,
    recommendedProducts,
    recommendedLoading,
    isWishlisted,
    wishlistLoading,
    user,
    
    // Computed values
    originalPrice,
    discountedPrice,
    hasDiscount,
    
    // Handlers
    handleQuantityChange,
    handleAddToCart,
    handleWishlistClick,
    handleImageChange,
    handleTabChange,
    handleBack,
    handleBackToProducts,
  };
};
